let cutsiteIdSeq = 0;
import circularSelector from "./circularSelector";
import sequenceSelector from "./sequenceSelector";
import restrictionEnzymesSelector from "./restrictionEnzymesSelector";
import cutsiteLabelColorSelector from "./cutsiteLabelColorSelector";
import { createSelector } from "reselect";
import { isEqual, flatMap as flatmap, map } from "lodash-es";
import { getCutsitesFromSequence } from "@teselagen/sequence-utils";
import { getLowerCaseObj } from "../utils/arrayUtils";
import featuresSelector from "./featuresSelector";
const cutsitesCache = [];
function getCachedResult(argsObj) {
  const idx = cutsitesCache.findIndex(
    (entry) => entry && isEqual(entry.args, argsObj)
  );
  if (idx === -1) return;
  const hit = cutsitesCache[idx];
  return hit.result;
}
function setCachedResult(argsObj, result, cacheSize = 50) {
  cutsitesCache.push({
    args: argsObj,
    result
  });
  if (cutsitesCache.length > cacheSize) cutsitesCache.shift();
}
import filteredRestrictionEnzymesSelector from "./filteredRestrictionEnzymesSelector";
export function cutsitesSelector(sequence, circular, enzymeList, cutsiteLabelColors, editorSize = 50, features = {}, filteredRestrictionEnzymes) {
  const importantRestrictionSites = Object.values(features || {}).filter(
    (f) => f.type === "restriction_site"
  );
  let filteredEnzymes;
  const hasFilter = filteredRestrictionEnzymes && Array.isArray(filteredRestrictionEnzymes);
  const isBroadFilter = !hasFilter || filteredRestrictionEnzymes.length === 0 || filteredRestrictionEnzymes.some(
    (e) => !e || e.value === "type2s" || e.value === "single" || e.value === "double" || e.value === "triple" || typeof e.value === "string" && e.value.includes("__userCreatedGroup")
  );
  if (isBroadFilter) {
    filteredEnzymes = map(enzymeList);
  } else {
    filteredEnzymes = [];
    filteredRestrictionEnzymes.forEach((e) => {
      if (!e || !e.value || e.value === "important") return;
      const lower = e.value.toLowerCase();
      if (enzymeList && enzymeList[lower]) {
        filteredEnzymes.push(enzymeList[lower]);
      }
    });
  }
  const importantEnzymes = importantRestrictionSites.map((f) => {
    let enzyme = f.restrictionEnzyme;
    if (!enzyme && f.notes && f.notes.restrictionEnzyme) {
      try {
        enzyme = JSON.parse(f.notes.restrictionEnzyme[0]);
      } catch (e) {
      }
    }
    if (!enzyme || !enzyme.site) {
      const name = (enzyme?.name || f.name || "").toLowerCase();
      if (enzymeList && enzymeList[name]) {
        enzyme = { ...enzymeList[name], ...enzyme };
      }
    }
    return enzyme;
  }).filter((e) => e && e.name && e.site);
  const combinedEnzymes = [...filteredEnzymes];
  importantEnzymes.forEach((ie) => {
    if (!combinedEnzymes.some(
      (e) => e.name.toLowerCase() === ie.name.toLowerCase()
    )) {
      combinedEnzymes.push(ie);
    }
  });
  if (combinedEnzymes.length === 0) {
    return {
      cutsitesByName: {},
      cutsitesById: {},
      cutsitesArray: []
    };
  }
  const cachedResult = getCachedResult({
    sequence,
    circular,
    enzymeList: combinedEnzymes,
    cutsiteLabelColors,
    features
  });
  if (cachedResult) {
    return cachedResult;
  }
  const allCutsitesByName = getLowerCaseObj(
    getCutsitesFromSequence(sequence, circular, combinedEnzymes)
  );
  const cutsitesByName = {};
  const cutsitesById = {};
  const importantMap = /* @__PURE__ */ new Map();
  importantRestrictionSites.forEach((f) => {
    const key = `${(f.name || "").toLowerCase()}_${Number(f.start)}_${Number(f.end)}`;
    importantMap.set(key, f);
  });
  const userFilterSet = new Set(filteredEnzymes.map((e) => e.name.toLowerCase()));
  Object.keys(allCutsitesByName).forEach(function(enzymeName) {
    let cutsitesForEnzyme = allCutsitesByName[enzymeName];
    const filteredCutsitesForEnzyme = cutsitesForEnzyme.filter((cutsite) => {
      const enzymeNameLower = cutsite.restrictionEnzyme?.name ? cutsite.restrictionEnzyme.name.toLowerCase() : "";
      const key = `${enzymeNameLower}_${Number(cutsite.recognitionSiteRange?.start)}_${Number(cutsite.recognitionSiteRange?.end)}`;
      const importantMatch = importantMap.get(key);
      if (importantMatch) {
        cutsite.isImportant = true;
        cutsite.importantFeatureId = importantMatch.id;
        return true;
      }
      return userFilterSet.has(enzymeName.toLowerCase());
    });
    if (filteredCutsitesForEnzyme.length > 0) {
      cutsitesByName[enzymeName] = filteredCutsitesForEnzyme;
      filteredCutsitesForEnzyme.forEach(function(cutsite, idx) {
        const numberOfCuts = cutsitesForEnzyme.length;
        const uniqueId = `cutsite_${enzymeName}_${cutsite.recognitionSiteRange?.start}_${cutsite.recognitionSiteRange?.end}_${idx || ++cutsiteIdSeq}`;
        cutsite.id = uniqueId;
        cutsite.numberOfCuts = numberOfCuts;
        cutsite.annotationType = "cutsite";
        cutsitesById[uniqueId] = cutsite;
        const mergedCutsiteColors = Object.assign(
          { single: "salmon", double: "lightblue", multi: "lightgrey" },
          cutsiteLabelColors
        );
        if (numberOfCuts === 1) {
          cutsite.labelColor = mergedCutsiteColors.single;
          cutsite.labelClassname = "singleCutter";
        } else if (numberOfCuts === 2) {
          cutsite.labelColor = mergedCutsiteColors.double;
          cutsite.labelClassname = "doubleCutter";
        } else {
          cutsite.labelColor = mergedCutsiteColors.multi;
          cutsite.labelClassname = "multiCutter";
        }
      });
    }
  });
  const cutsitesArray = flatmap(cutsitesByName, function(cutsitesForEnzyme) {
    return cutsitesForEnzyme;
  });
  const result = {
    cutsitesByName,
    cutsitesById,
    cutsitesArray
  };
  setCachedResult(
    {
      sequence,
      circular,
      enzymeList: combinedEnzymes,
      cutsiteLabelColors,
      features
    },
    result,
    editorSize
  );
  return result;
}
export const allCutsitesSelector = createSelector(
  sequenceSelector,
  circularSelector,
  restrictionEnzymesSelector,
  cutsiteLabelColorSelector,
  (editorState) => editorState.editorSize,
  featuresSelector,
  cutsitesSelector
);
export default createSelector(
  sequenceSelector,
  circularSelector,
  restrictionEnzymesSelector,
  cutsiteLabelColorSelector,
  (editorState) => editorState.editorSize,
  featuresSelector,
  filteredRestrictionEnzymesSelector,
  cutsitesSelector
);
